<template>
<div id="music-playlist" class="music">
    <div class="m-t-nav">
        <div class="big-m-t">

            <Playlist
            :playlist-position="0"
            :playlist="listData"
            :listType="'songs'"
            :editButtons="false"
            :title="false"
            :subTitle="subTitle"
            :key="reloadComponent"
            :view="'grid'"
            />

        </div>
    </div>
</div>
</template>

<script>
import { rtdb } from '@/db.js'
import { mapGetters } from "vuex"
import { sanitizeKey } from '@/utils/helpers.js'
import Playlist from '@/components/playlists/Playlist.vue'

export default {
  name: 'MusicPlaylist',
  components: {
    Playlist
  },
  metaInfo: {
    title: "Playlist",
  },
  data() {
	  return {
        listData: {
          songs: null
        },
        subTitle: null,
        reloadComponent: false,
        locationPrefixCache: {} // Cache to avoid repeated database queries
	  }
  },
  created () {
    // fetch the data when the view is created and the data is
    // already being observed
    this.getProfilesWorld()
  },
  watch: {
    // call again the method if the route changes
    '$route': 'getProfilesWorld'

  },
  computed: {
    ...mapGetters([
      'getUser',
      'getUserSettings',
    ]),
  },
  methods: {
    async getProfilesWorld() {
      if (this.$route.params.geoMatch) {
        const locationName = this.$route.params.geoMatch
        if (locationName && locationName.toLowerCase() === 'unranked') {
          this.getUnrankedSongs()
          return
        }
        await this.getSongsByLocation(locationName)
        return
      }
      if (this.$route.params.genreMatch) {
        const genreName = this.$route.params.genreMatch
        await this.getSongsByGenre(genreName)
      }
    },

    async getSongsByLocation(locationName) {
      console.log(`🔍 Finding rank data for location: ${locationName}`)
      
      // Find which location level has data
      const rankPrefix = await this.findRankPrefix(locationName)
      
      if (!rankPrefix) {
        console.warn(`⚠️ No rank data found for location: ${locationName}`)
        this.listData.songs = []
        this.subTitle = '0 tracks'
        return
      }

      const sanitizedLocation = sanitizeKey(locationName)
      const rankPath = `ranks/${rankPrefix}${sanitizedLocation}`

      console.log(`✅ Using rank path: ${rankPath}`)

      // Query all ranked songs for this location
      rtdb.ref('rankSongs')
        .orderByChild(rankPath)
        .startAt(1)
        .once('value', (snapshot) => {
          const results = []
          
          snapshot.forEach((child) => {
            const val = child.val()
            
            // Format data for Playlist component (songs)
            results.push({
              ...val.songSettings,
              key: child.key,
              rankTotal: val.rankTotal,
              user: val.userSettings,
              categoryRank: val.ranks?.[`${rankPrefix}${sanitizedLocation}`]
            })
          })

          console.log(`✅ Found ${results.length} ${locationName} tracks`)
          
          this.listData.songs = results
          this.subTitle = `${results.length} tracks`
          this.reloadComponent = !this.reloadComponent
        })
        .catch(error => {
          console.error(`❌ Error fetching ${locationName} songs:`, error)
        })
    },

    async getSongsByGenre(genreName) {
      const sanitizedGenre = sanitizeKey(genreName)
      const rankPath = `ranks/${sanitizedGenre}` // Genres don't use prefix

      console.log(`🔍 Querying rankSongs for genre ${genreName}: orderByChild("${rankPath}")`)

      // Query all ranked songs for this genre
      rtdb.ref('rankSongs')
        .orderByChild(rankPath)
        .startAt(1)
        .once('value', (snapshot) => {
          const results = []
          
          snapshot.forEach((child) => {
            const val = child.val()
            
            // Format data for Playlist component (songs)
            results.push({
              ...val.songSettings,
              key: child.key,
              rankTotal: val.rankTotal,
              user: val.userSettings,
              categoryRank: val.ranks?.[sanitizedGenre]
            })
          })

          console.log(`✅ Found ${results.length} ${genreName} tracks`)
          
          this.listData.songs = results
          this.subTitle = `${results.length} tracks`
          this.reloadComponent = !this.reloadComponent
        })
        .catch(error => {
          console.error(`❌ Error fetching ${genreName} songs:`, error)
        })
    },

    getUnrankedSongs() {
      rtdb.ref('rankSongs')
        .orderByChild('ratio')
        .equalTo(0)
        .once('value', (snapshot) => {
          const results = []
          snapshot.forEach((child) => {
            const val = child.val()
            results.push({
              ...val.songSettings,
              key: child.key,
              rankTotal: val.rankTotal,
              user: val.userSettings,
              categoryRank: null
            })
          })
          this.listData.songs = results
          this.subTitle = `${results.length} tracks`
          this.reloadComponent = !this.reloadComponent
        })
        .catch(error => {
          console.error('❌ Error fetching Unranked songs:', error)
        })
    },

    /**
     * Dynamically finds which location level (loc0_, loc1_, loc2_, loc3_) has data
     * Uses caching to avoid repeated database queries
     */
    async findRankPrefix(locationName) {
      const sanitizedLocation = sanitizeKey(locationName)
      
      // Check cache first
      const cacheKey = sanitizedLocation
      if (this.locationPrefixCache[cacheKey]) {
        console.log(`📦 Using cached prefix for ${locationName}`)
        return this.locationPrefixCache[cacheKey]
      }
      
      const prefixes = ['loc0_', 'loc1_', 'loc2_', 'loc3_']
      
      // Special case: "world" always uses loc0_
      if (locationName.toLowerCase() === 'world') {
        this.locationPrefixCache[cacheKey] = 'loc0_'
        return 'loc0_'
      }

      // Try each prefix and see which one has data
      for (const prefix of prefixes) {
        const rankPath = `ranks/${prefix}${sanitizedLocation}`
        
        try {
          const snapshot = await rtdb.ref('rankSongs')
            .orderByChild(rankPath)
            .startAt(1)
            .limitToFirst(1)
            .once('value')
          
          // If we found at least one result, this is the correct prefix
          if (snapshot.exists() && snapshot.numChildren() > 0) {
            console.log(`✅ Found data with prefix: ${prefix}`)
            // Cache the result
            this.locationPrefixCache[cacheKey] = prefix
            return prefix
          }
        } catch (error) {
          console.error(`Error checking prefix ${prefix}:`, error)
        }
      }
      
      // No data found with any prefix
      return null
    },
  }

}
</script>
<style lang="scss" scoped>
</style>